import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { FileText, Plus, Trash2, CheckCircle2, Pencil, Upload, Image as ImageIcon } from 'lucide-react';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LetterheadPage() {
  const [letterheads, setLetterheads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const logoFileRef = useRef(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    company_name: '', logo_url: '', tagline: '', address: '',
    phone: '', email: '', website: '', primary_color: '#10B981', footer_text: '', is_active: false,
  });

  const fetch = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/sales/letterheads`);
      setLetterheads(data);
    } catch (e) {
      console.error('Letterhead load error:', e?.response?.status);
      setLetterheads([]);
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const openCreate = () => {
    setEditing(null);
    setForm({ company_name: '', logo_url: '', tagline: '', address: '', phone: '', email: '', website: '', primary_color: '#10B981', footer_text: '', is_active: letterheads.length === 0 });
    setShowModal(true);
  };

  const openEdit = (lh) => {
    setEditing(lh);
    setForm({ company_name: lh.company_name || '', logo_url: lh.logo_url || '', tagline: lh.tagline || '', address: lh.address || '', phone: lh.phone || '', email: lh.email || '', website: lh.website || '', primary_color: lh.primary_color || '#10B981', footer_text: lh.footer_text || '', is_active: lh.is_active || false });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.company_name.trim()) { toast.error('Company name required'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/api/sales/letterheads/${editing.id}`, form);
        toast.success('Letterhead updated');
      } else {
        await axios.post(`${API}/api/sales/letterheads`, form);
        toast.success('Letterhead created');
      }
      setShowModal(false);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const activate = async (id) => {
    try {
      await axios.post(`${API}/api/sales/letterheads/${id}/activate`);
      toast.success('Letterhead activated');
      fetch();
    } catch { toast.error('Failed to activate'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this letterhead?')) return;
    try {
      await axios.delete(`${API}/api/sales/letterheads/${id}`);
      toast.success('Deleted');
      fetch();
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="letterhead-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center"><FileText className="h-5 w-5 text-indigo-500" /></div>
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Letterhead System<PageInfoTooltip pageKey="letterhead" /></h1>
            <p className="text-xs text-gray-400">Manage company branding for proposals, quotes, and invoices</p>
          </div>
        </div>
        <Button size="sm" onClick={openCreate} data-testid="add-letterhead-btn"><Plus className="h-3.5 w-3.5 mr-1" />New Letterhead</Button>
      </div>

      {letterheads.length === 0 ? (
        <Card className="bg-white border-dashed border-2 border-gray-200">
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto mb-3 text-gray-200" />
            <p className="text-sm text-gray-500 mb-2">No letterheads created yet</p>
            <p className="text-xs text-gray-400 mb-4">Create a letterhead to brand your proposals and quotes with your company logo, colors, and contact info.</p>
            <Button size="sm" onClick={openCreate}>Create Your First Letterhead</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {letterheads.map(lh => (
            <Card key={lh.id} className={cn("bg-white transition-colors", lh.is_active ? "border-emerald-300 ring-1 ring-emerald-100" : "border-gray-border")}>
              <CardContent className="p-5">
                {/* Preview */}
                <div className="border rounded-lg p-4 mb-4 bg-white" style={{ borderTopColor: lh.primary_color, borderTopWidth: '4px' }}>
                  <div className="flex items-center gap-3">
                    {lh.logo_url ? <img src={lh.logo_url} alt="" className="h-8 object-contain" /> : null}
                    <div>
                      <h3 className="font-bold" style={{ color: lh.primary_color }}>{lh.company_name}</h3>
                      {lh.tagline && <p className="text-[10px] text-gray-400">{lh.tagline}</p>}
                    </div>
                  </div>
                  <div className="flex gap-4 mt-2 text-[10px] text-gray-400">
                    {lh.phone && <span>{lh.phone}</span>}
                    {lh.email && <span>{lh.email}</span>}
                    {lh.website && <span>{lh.website}</span>}
                  </div>
                  {lh.footer_text && <p className="text-[9px] text-gray-300 mt-2 border-t pt-1">{lh.footer_text}</p>}
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {lh.is_active ? (
                      <Badge className="bg-emerald-100 text-emerald-700 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" />Active</Badge>
                    ) : (
                      <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => activate(lh.id)}>Set as Active</Button>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(lh)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600" onClick={() => remove(lh.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit' : 'Create'} Letterhead</DialogTitle>
            <DialogDescription>This letterhead will be used on all proposals, quotes, and invoices when active.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Company Name *</Label>
              <Input value={form.company_name} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))} className="mt-1" data-testid="lh-company-name" /></div>
            <div>
              <Label className="text-xs">Logo</Label>
              <div className="mt-1 flex items-center gap-3">
                <div className="h-14 w-20 rounded border bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
                  {form.logo_url
                    ? <img src={form.logo_url} alt="logo" className="max-h-full max-w-full object-contain" />
                    : <ImageIcon className="h-6 w-6 text-gray-300" />}
                </div>
                <input
                  ref={logoFileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 1_500_000) { toast.error('Logo too large (max 1.5 MB)'); return; }
                    const r = new FileReader();
                    r.onload = () => setForm(p => ({ ...p, logo_url: r.result }));
                    r.readAsDataURL(f);
                  }}
                  data-testid="lh-logo-file"
                />
                <div className="flex flex-col gap-1">
                  <Button type="button" variant="outline" size="sm" onClick={() => logoFileRef.current?.click()} data-testid="lh-logo-upload-btn">
                    <Upload className="h-4 w-4 mr-1" /> {form.logo_url ? 'Replace' : 'Upload'}
                  </Button>
                  {form.logo_url && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => setForm(p => ({ ...p, logo_url: '' }))} data-testid="lh-logo-clear-btn">
                      Remove
                    </Button>
                  )}
                </div>
              </div>
              <p className="mt-1 text-[10px] text-gray-500">PNG / SVG with transparency works best · max 1.5 MB.</p>
            </div>
            <div><Label className="text-xs">Tagline</Label>
              <Input value={form.tagline} onChange={e => setForm(p => ({ ...p, tagline: e.target.value }))} placeholder="Your company tagline" className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Phone</Label>
                <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-xs">Email</Label>
                <Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="mt-1" /></div>
            </div>
            <div><Label className="text-xs">Website</Label>
              <Input value={form.website} onChange={e => setForm(p => ({ ...p, website: e.target.value }))} placeholder="https://www.example.com" className="mt-1" /></div>
            <div><Label className="text-xs">Address</Label>
              <Textarea value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} rows={2} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Brand Color</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={form.primary_color} onChange={e => setForm(p => ({ ...p, primary_color: e.target.value }))} className="w-10 h-8 rounded border cursor-pointer" />
                  <Input value={form.primary_color} onChange={e => setForm(p => ({ ...p, primary_color: e.target.value }))} className="h-8 text-xs" />
                </div>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} className="rounded" />
                  <span className="text-xs">Set as active letterhead</span>
                </label>
              </div>
            </div>
            <div><Label className="text-xs">Footer Text</Label>
              <Input value={form.footer_text} onChange={e => setForm(p => ({ ...p, footer_text: e.target.value }))} placeholder="e.g. Thank you for your business!" className="mt-1" /></div>
            <Button className="w-full" onClick={save} data-testid="save-letterhead-btn">{editing ? 'Update' : 'Create'} Letterhead</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
